import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { reportClientError } from '../utils/clientLogger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROOM_COLORS = {
  living_room: '#E8F5E9',
  bedroom: '#E3F2FD',
  kitchen: '#FFF3E0',
  bathroom: '#E0F7FA',
  dining_room: '#FFF8E1',
  office: '#F3E5F5',
  hallway: '#ECEFF1',
  garage: '#CFD8DC',
  laundry: '#E1F5FE',
  balcony: '#F1F8E9'
};

const ROOM_LABELS = {
  living_room: 'Sala de Estar',
  bedroom: 'Quarto',
  kitchen: 'Cozinha',
  bathroom: 'Banheiro',
  dining_room: 'Sala de Jantar',
  office: 'Escritório',
  hallway: 'Corredor',
  garage: 'Garagem',
  laundry: 'Lavanderia',
  balcony: 'Varanda'
};

const PROJECT_VIEW_CANVAS_WIDTH = 800;
const PROJECT_VIEW_CANVAS_HEIGHT = 600;

const fetchProjectById = async (projectId) => {
  const response = await axios.get(`${API}/projects/${projectId}`);
  return response.data;
};

const drawProjectFloorPlan = (canvas, floorPlan) => {
  const ctx = canvas.getContext('2d');
  const gridSize = floorPlan.scale || 20;

  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, PROJECT_VIEW_CANVAS_WIDTH, PROJECT_VIEW_CANVAS_HEIGHT);

  ctx.strokeStyle = '#E5E7EB';
  ctx.lineWidth = 0.5;

  for (let x = 0; x <= PROJECT_VIEW_CANVAS_WIDTH; x += gridSize) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, PROJECT_VIEW_CANVAS_HEIGHT);
    ctx.stroke();
  }

  for (let y = 0; y <= PROJECT_VIEW_CANVAS_HEIGHT; y += gridSize) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(PROJECT_VIEW_CANVAS_WIDTH, y);
    ctx.stroke();
  }

  floorPlan.rooms?.forEach((room) => {
    if (!room.points || room.points.length <= 2) {
      return;
    }

    ctx.fillStyle = room.color || ROOM_COLORS[room.room_type] || '#E0E0E0';
    ctx.beginPath();
    ctx.moveTo(room.points[0].x, room.points[0].y);
    room.points.forEach((point, index) => {
      if (index > 0) {
        ctx.lineTo(point.x, point.y);
      }
    });
    ctx.closePath();
    ctx.fill();

    const centerX = room.points.reduce((sum, point) => sum + point.x, 0) / room.points.length;
    const centerY = room.points.reduce((sum, point) => sum + point.y, 0) / room.points.length;
    ctx.fillStyle = '#374151';
    ctx.font = 'bold 12px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(room.name, centerX, centerY);
  });

  if (floorPlan.walls) {
    ctx.strokeStyle = '#1F2937';
    ctx.lineWidth = 8;
    ctx.lineCap = 'round';
    floorPlan.walls.forEach((wall) => {
      ctx.beginPath();
      ctx.moveTo(wall.start_x, wall.start_y);
      ctx.lineTo(wall.end_x, wall.end_y);
      ctx.stroke();
    });
  }
};

const ProjectView = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadProject = useCallback(async () => {
    try {
      const projectData = await fetchProjectById(projectId);
      setProject(projectData);
    } catch (err) {
      reportClientError('Error loading project:', err);
      setError('Projeto não encontrado');
    } finally {
      setLoading(false);
    }
  }, [projectId, fetchProjectById, reportClientError, setError, setLoading]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const floorPlan = project?.floor_plan;
    if (!canvas || !floorPlan) return;

    drawProjectFloorPlan(canvas, floorPlan);
  }, [project, drawProjectFloorPlan, canvasRef]);

  useEffect(() => {
    if (project?.floor_plan) {
      drawCanvas();
    }
  }, [project, drawCanvas]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando projeto...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">❌</div>
          <p className="text-gray-600 mb-4" data-testid="project-view-error-message">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            data-testid="project-view-error-back-button"
          >
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-600 hover:text-gray-900"
            data-testid="project-view-back-button"
            >
              ← Voltar
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{project?.name}</h1>
              <p className="text-sm text-gray-500">Criado em {new Date(project?.created_at).toLocaleDateString('pt-BR')}</p>
            </div>
          </div>
          <button
            onClick={() => navigate(`/render-3d/${projectId}`)}
            className="px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-blue-700 transition"
            data-testid="project-view-open-3d-button"
          >
            🏠 Ver sua casa em 3D
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Floor Plan */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Planta Baixa</h2>
            <canvas
              ref={canvasRef}
              width={PROJECT_VIEW_CANVAS_WIDTH}
              height={PROJECT_VIEW_CANVAS_HEIGHT}
              className="w-full border border-gray-200 rounded-lg bg-white"
            />
          </div>

          {/* Project Info */}
          <div className="space-y-6">
            {/* Stats */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">📊 Informações</h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-600">Cômodos</span>
                  <span className="font-semibold text-gray-900" data-testid="project-view-rooms-count">{project?.floor_plan?.rooms?.length || 0}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-600">Paredes</span>
                  <span className="font-semibold text-gray-900" data-testid="project-view-walls-count">{project?.floor_plan?.walls?.length || 0}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-600">Visualizações 3D</span>
                  <span className="font-semibold text-purple-600" data-testid="project-view-views-count">{project?.view_count_3d || 0}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">Usuário</span>
                  <span className="font-semibold text-gray-900" data-testid="project-view-user-name">{project?.user_name || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Rooms List */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">🏠 Cômodos</h2>
              <div className="space-y-2">
                {project?.floor_plan?.rooms?.length > 0 ? (
                  project.floor_plan.rooms.map((room, index) => (
                    <div key={room.id || index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: room.color || ROOM_COLORS[room.room_type] || '#E0E0E0' }}
                      />
                      <div>
                        <p className="font-medium text-gray-900">{room.name}</p>
                        <p className="text-sm text-gray-500">{ROOM_LABELS[room.room_type] || room.room_type}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">Nenhum cômodo definido</p>
                )}
              </div>
            </div>

            {/* Description */}
            {project?.description && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">📝 Descrição</h2>
                <p className="text-gray-600">{project.description}</p>
              </div>
            )}

            {/* Actions */}
            <div className="bg-gradient-to-r from-purple-500 to-blue-600 rounded-xl shadow-sm p-6 text-white">
              <h2 className="text-lg font-semibold mb-4">🚀 Ações</h2>
              <div className="space-y-3">
                <button
                  onClick={() => navigate(`/render-3d/${projectId}`)}
                  className="w-full py-3 bg-white/20 rounded-lg hover:bg-white/30 transition flex items-center justify-center gap-2"
                  data-testid="project-view-actions-view-3d-button"
                >
                  🏠 Ver em 3D
                </button>
                <button
                  onClick={() => navigate('/desenhar-planta')}
                  className="w-full py-3 bg-white/20 rounded-lg hover:bg-white/30 transition flex items-center justify-center gap-2"
                  data-testid="project-view-actions-edit-plan-button"
                >
                  ✏️ Editar Planta
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectView;
